import { useEnvironmentInterface } from "environment-interface";
import { useEditor, useEditorFeature } from "features/editor/Feature";
import { useSession } from "hooks/api/useSession";
import { createBranch } from "queries/project";
import { DisposableStore } from "vscode/monaco";

import { useActiveFile } from "hooks/useActiveFile";
import { useBranchingAnimation } from "hooks/useBranchingAnimation";
import { slugifyBranchName } from "utils/git";

import type { Command } from "../../types";

// Joke branch names
const PLACEHOLDER_BRANCHES = [
  "feat/the-best-feature",
  "fix/next-bug",
  "perf/n-1-queries",
  "refactor/replace-tabs-with-spaces",
  "refactor/remove-semicolons",
];

function validateBranchName(value: string) {
  if (value === "") {
    return "Please enter a branch name";
  }

  if (/\s/.test(value)) {
    return "Branch names cannot contain spaces";
  }

  return undefined;
}

export const useToggleBranchCreatorCommand = (): Command => {
  const { monacoEditor } = useEnvironmentInterface();
  const [session, sessionApi] = useSession();
  const { startBranchingAnimation } = useBranchingAnimation();
  const [_, editorDispatch] = useEditorFeature();
  const [__, activeFileActions] = useActiveFile();
  const editor = useEditor();

  async function switchBranch(create: boolean, branchName: string) {
    if (session.state !== "AUTHENTICATED") {
      sessionApi.signIn({
        type: "github",
      });

      return;
    }

    if (editor.type === "sandbox" || editor.workspace == null) {
      throw new Error("Cannot switch branches without a workspace");
    }

    startBranchingAnimation();

    activeFileActions.unset();

    if (create) {
      await createBranch({
        owner: editor.owner,
        repo: editor.repo,
        branch: branchName,
        sourceBranch: editor.branch,
        workspaceId: editor.workspace.id,
      });
    }

    editorDispatch({
      type: "SWITCH_BRANCH",
      name: branchName,
      create,
    });
  }

  const command: Command = {
    id: "codesandbox.git.createBranch",
    section: "Git",
    name: "Create branch...",
    perform: async () => {
      const extensionApi = await monacoEditor.getExtensionApi();
      const disposableStore = new DisposableStore();

      const inputBox = disposableStore.add(
        extensionApi.window.createInputBox(),
      );

      inputBox.ignoreFocusOut = true;
      inputBox.placeholder =
        PLACEHOLDER_BRANCHES[
          Math.floor(Math.random() * PLACEHOLDER_BRANCHES.length)
        ];
      inputBox.title = "Choose New Branch Name";
      inputBox.value = "";
      inputBox.show();

      disposableStore.add(
        inputBox.onDidChangeValue(() => {
          const validationMessage = validateBranchName(inputBox.value);
          if (validationMessage) {
            inputBox.validationMessage = validationMessage;
          } else {
            inputBox.validationMessage = undefined;
          }
        }),
      );

      disposableStore.add(
        inputBox.onDidAccept(async () => {
          if (!inputBox.value) {
            return;
          }

          inputBox.busy = true;
          try {
            await switchBranch(true, slugifyBranchName(inputBox.value));
            inputBox.hide();
          } catch (e) {
            if (e instanceof Error) {
              inputBox.validationMessage =
                "Error creating branch: " + e.message;
            }
          }
          inputBox.busy = false;
        }),
      );

      disposableStore.add(
        inputBox.onDidHide(() => {
          disposableStore.dispose();
        }),
      );
    },
  };

  return command;
};
